/**
 * Hiring-demand board — pure shaping. No React, no I/O, so the awkward parts
 * (which column a role lands in, what a collapsed group adds up to) are testable
 * on their own.
 */
import type {
  BoardRole,
  DemandBoard,
  DemandMonth,
  HdbStatus,
  RosterMember,
} from '../../../types/taDemand';
import { isDraft } from '../../../types/taDemand';
import { levelRank } from './demandLevels';
/*
 * The tree half lives next door (demandTreeModel.ts, split at the 500-line
 * ceiling). It is both IMPORTED and re-exported: `export *` alone does not bind
 * the names in this module's own scope, and `summarise` calls `rolesOfDept`.
 */
import { rolesOfDept, type DeptNode } from './demandTreeModel';

export * from './demandTreeModel';

/** A board column: either one month, or the collapsed run of past months. */
export type Track =
  | { kind: 'month'; month: DemandMonth }
  | { kind: 'group'; months: DemandMonth[]; label: string };

export function buildTracks(months: DemandMonth[], showPast: boolean): Track[] {
  const past = months.filter((m) => m.past);
  const rest = months.filter((m) => !m.past).map((month) => ({ kind: 'month' as const, month }));
  if (showPast || past.length === 0) {
    return months.map((month) => ({ kind: 'month' as const, month }));
  }
  const label =
    past.length === 1
      ? `${past[0].label}`
      : `${past[0].label}–${past[past.length - 1].label}`;
  return [{ kind: 'group', months: past, label }, ...rest];
}

export const trackMonthKeys = (t: Track): string[] =>
  t.kind === 'group' ? t.months.map((m) => m.key) : [t.month.key];

export const trackBand = (t: Track) => (t.kind === 'group' ? 'past' : t.month.band);

export const heads = (roles: BoardRole[]) => roles.reduce((s, r) => s + r.count, 0);

/** Current FTE on a unit — what the team actually has today. */
/**
 * What a unit actually has today, split three ways.
 *
 * ⚠️ `fte` is INTERNAL HEADCOUNT ONLY. An external is here and working but is
 * not headcount — employed through an EOR — and somebody on parental leave is
 * employed but not at work. Adding either to the figure a lead plans against is
 * the mistake this split exists to prevent, and both are still shown, marked
 * and counted BESIDE it rather than hidden: a backfill decision needs to know
 * they are there.
 */
export function rosterSplit(members: RosterMember[]) {
  let fte = 0;
  let externalFte = 0;
  let onLeaveFte = 0;
  let external = 0;
  let onLeave = 0;
  for (const m of members) {
    const v = Number(m.fte) || 1;
    if (m.external) {
      externalFte += v;
      external += 1;
    } else if (m.onLeave) {
      onLeaveFte += v;
      onLeave += 1;
    } else {
      fte += v;
    }
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    people: members.length,
    fte: r2(fte),
    external,
    externalFte: r2(externalFte),
    onLeave,
    onLeaveFte: r2(onLeaveFte),
  };
}

/**
 * A team, juniors first.
 *
 * Sorted by IC level rather than by whatever order Airtable returned, which was
 * effectively random and made a team of ten unreadable. Ties break on the name,
 * so the order is stable between loads — a list that reshuffles itself is one
 * nobody can point at.
 */
export function sortedMembers(members: RosterMember[]): RosterMember[] {
  return [...members].sort(
    (a, b) => levelRank(a.level) - levelRank(b.level) || a.name.localeCompare(b.name),
  );
}

/** Internal FTE on a unit — the number a lead plans against. */
export const rosterFte = (members: RosterMember[]) => rosterSplit(members).fte;

/** Backfill drafts already raised, keyed by the seat they replace. */
export function backfillsByEmployee(board: DemandBoard): Map<string, string> {
  const out = new Map<string, string>();
  for (const d of board.drafts) {
    if (d.employeeId) out.set(d.employeeId, d.id);
  }
  return out;
}
export const fteOf = (roles: BoardRole[]) => roles.reduce((s, r) => s + r.count * r.fte, 0);

export interface CellTally {
  hdb: number;
  drafts: number;
  hired: number;
  total: number;
  /**
   * Headcount that was planned to have started by this month and has not.
   * Negative, and it CARRIES FORWARD: a July seat still empty in October counts
   * against July, August, September and October — the same running shortfall
   * Felix's Ops reforecast reports as its delta row.
   */
  delta: number;
}

/**
 * Where a role's bar starts and ends.
 *
 * A role that arrived when it was planned is a single point. A role that slipped
 * is a SPAN — from the month it was promised to the month it actually landed, or
 * to today when the seat is still empty. Drawing the wait is the point: "planned
 * for August, still nobody" is invisible as a dot sitting on August.
 */
export interface RoleSpan {
  from: string;
  to: string;
  /** Months of slip. 0 means it landed on plan and draws as a dot. */
  months: number;
  /** True while the seat is still empty — the bar has no closing end. */
  openEnded: boolean;
}

export function roleSpan(
  role: BoardRole,
  months: DemandMonth[],
  currentMonth: string,
): RoleSpan {
  // An undated role has nothing to be late against.
  if (!role.month && !role.plannedMonth) {
    return { from: '', to: '', months: 0, openEnded: false };
  }
  const planned = (role.plannedMonth || role.month) as string;
  const filled = !isDraft(role) && role.status === 'hired' ? role.actualMonth || role.month : null;

  // An ON HOLD role is never late. It was paused on purpose and will be given a
  // new start date when someone decides to recruit for it again, so the date it
  // currently carries is provisional — drawing a red overdue bar against it
  // would report a deliberate decision as a TA failure.
  if (!isDraft(role) && role.status === 'hold') {
    return { from: planned, to: planned, months: 0, openEnded: false };
  }

  if (filled) {
    // Landed. Late only if it landed after it was promised.
    const late = filled > planned;
    return { from: late ? planned : filled, to: filled, months: late ? gap(planned, filled) : 0, openEnded: false };
  }

  // Still empty. The bar runs from the ORIGINAL commitment to whichever is
  // later: today (how long the team has actually waited) or the revised plan
  // (how much longer it is expected to wait). The slip is counted only up to
  // today — nobody is late for a month that has not happened.
  const adjusted = !isDraft(role) ? role.adjustedMonth : null;
  if (planned < currentMonth) {
    const reach = adjusted && adjusted > currentMonth ? adjusted : currentMonth;
    const onBoard = months.some((m) => m.key === reach);
    const lastColumn = months[months.length - 1]?.key;
    return {
      from: planned,
      to: onBoard ? reach : (lastColumn ?? currentMonth),
      months: gap(planned, currentMonth),
      openEnded: true,
    };
  }
  // Not due yet, but the plan has already moved: show the move, without
  // claiming anyone is late.
  if (adjusted && adjusted > planned) {
    const lastColumn = months[months.length - 1]?.key;
    const onBoard = months.some((m) => m.key === adjusted);
    return { from: planned, to: onBoard ? adjusted : (lastColumn ?? adjusted), months: 0, openEnded: false };
  }
  return { from: planned, to: planned, months: 0, openEnded: false };
}

/**
 * An overdue, still-empty bar stops at TODAY.
 *
 * ⚠️ `roleSpan` runs an open-ended bar to whichever is LATER: today, or the
 * re-planned date. On the demand board that is deliberate — "how much longer it
 * is expected to wait" is a planning fact. On TRACKING it reads as a lie: the
 * red line is how long the seat has been empty, and a role re-dated to January
 * drew a red bar four months into the future, through months in which nothing
 * has happened yet (Theresa, Sep 2026).
 *
 * Applied at the Tracking view rather than inside `roleSpan` so the two boards
 * keep their own meanings rather than one silently changing the other. `months`
 * is untouched: the SLIP was already counted only up to today.
 */
export function clampOpenEndedToToday(span: RoleSpan, currentMonth: string): RoleSpan {
  if (!span.openEnded || !currentMonth || span.to <= currentMonth) return span;
  // Never before the bar's own start — a seat planned for a future month that
  // somehow reads open-ended must not be given a backwards span.
  return { ...span, to: currentMonth < span.from ? span.from : currentMonth };
}

/** Whole months between two YYYY-MM keys. */
export function gap(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  if (!fy || !ty) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}

/** Does this role's bar cover the given month? */
export const spanCovers = (span: RoleSpan, key: string) => key >= span.from && key <= span.to;

/** The tracks a span covers, so a bar can be drawn across a collapsed group too. */
export function spanCoversTrack(span: RoleSpan, t: Track): boolean {
  return trackMonthKeys(t).some((k) => spanCovers(span, k));
}

export const isLate = (span: RoleSpan) => span.months > 0;

/**
 * Does this span need a bar at all? True whenever the role does not sit where it
 * was promised — including the case where the plan has ALREADY been moved to a
 * future month, which is a slip nobody is late for yet.
 */
export const hasBar = (span: RoleSpan) => span.from !== span.to;

export function tally(
  roles: BoardRole[],
  tracks: Track[],
  months: DemandMonth[] = [],
  currentMonth = '',
): CellTally[] {
  return tracks.map((t) => {
    const keys = new Set(trackMonthKeys(t));
    let hdb = 0;
    let drafts = 0;
    let hired = 0;
    let delta = 0;
    for (const r of roles) {
      if (r.month && keys.has(r.month)) {
        if (isDraft(r)) drafts += r.count;
        else {
          hdb += r.count;
          if (r.status === 'hired') hired += r.count;
        }
      }
      // The shortfall is judged per month covered by the wait, not per month the
      // role sits in — which is what makes it carry forward.
      if (months.length && currentMonth) {
        const span = roleSpan(r, months, currentMonth);
        if (span.months > 0) {
          // Count the months of the wait that fall inside this column, excluding
          // the month it finally landed in: by then the seat is no longer empty.
          const owed = trackMonthKeys(t).filter(
            (k) => k >= span.from && (span.openEnded ? k <= span.to : k < span.to),
          );
          if (owed.length) delta -= r.count;
        }
      }
    }
    return { hdb, drafts, hired, total: hdb + drafts, delta };
  });
}

/**
 * Roles TA still has to deliver: HDB rows whose status is in `demand`, plus every
 * draft. `demand` excludes `hold` — see DEMAND_STATUSES in taDemand.js.
 */
export const outstanding = (roles: BoardRole[], demand: HdbStatus[]): BoardRole[] =>
  roles.filter((r) => (isDraft(r) ? true : demand.includes(r.status)));

/**
 * The board's KPIs.
 *
 * Every one of these is a DEFINITION, and getting them wrong is worse than not
 * showing them — so each is stated here and repeated in the tile's own tooltip.
 * The corrections behind this version (Theresa, Sep 2026):
 *
 * - A hire counts when the OFFER IS ACCEPTED, not when the person starts.
 *   Counting by start date delayed every hire by its notice period and lost
 *   anyone signed but not yet begun, which is why "this trimester" read 0.
 * - `Initiative application` is filtered out server-side: it is a standing
 *   pipeline, not a vacancy.
 * - Roles with no date, or a date outside the window, are still DEMAND. They
 *   are counted here even though they draw no marker, so this figure agrees
 *   with the same filter run in Airtable.
 */
export function summarise(board: DemandBoard, tree: DeptNode[]) {
  const all = tree.flatMap(rolesOfDept);
  const year = board.months[0]?.key.slice(0, 4) ?? '';
  const lookahead = board.months.filter((m) => m.band === 'lookahead').map((m) => m.key);

  /** Won this calendar year, by the month the offer was accepted. */
  const hiredYtd = all.filter(
    (r) => !isDraft(r) && r.status === 'hired' && (r.offerAcceptedMonth ?? '').startsWith(year),
  );
  /** Won since the trimester opened. */
  const hiredTrimester = hiredYtd.filter(
    (r) => !isDraft(r) && (r.offerAcceptedMonth ?? '') >= board.trimesterStart,
  );

  const toRecruitRoles = outstanding(all, board.demandStatuses);
  const draftRoles = all.filter(isDraft);
  const inLookahead = all.filter((r) => r.month && lookahead.includes(r.month));

  /**
   * The heaviest single month ahead, by the date the role is CURRENTLY expected
   * — the adjusted plan where there is one, else the original. Undated roles
   * cannot land in a month and are excluded from this one figure only.
   */
  const visible = board.months.filter((m) => !m.past);
  const expectedMonth = (r: BoardRole) => r.adjustedMonth || r.plannedMonth || r.month;
  const busiest = visible
    .map((m) => ({ month: m, n: heads(toRecruitRoles.filter((r) => expectedMonth(r) === m.key)) }))
    .sort((a, b) => b.n - a.n)[0];

  return {
    hiredYtd: heads(hiredYtd),
    hiredTrimester: heads(hiredTrimester),
    lookahead: heads(inLookahead),
    toRecruit: heads(toRecruitRoles),
    drafts: heads(draftRoles),
    undated: heads(toRecruitRoles.filter((r) => !r.month)),
    openFte: fteOf(toRecruitRoles),
    busiest,
  };
}
